import { FC, useEffect } from 'react';
import {
	Autocomplete,
	Box,
	Button,
	Checkbox,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControlLabel,
	TextField,
} from '@mui/material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useDispatch, useSelector } from 'react-redux';
import {
	createSpecialTerritoryRequest,
	resetSaveSpecialTerritorySuccess,
	updateSpecialTerritoryRequest,
} from 'redux/reducers/taxDuties/reducer';
import {
	getSpecialTerritorySaveLoading,
	getSpecialTerritorySaveSuccess,
} from 'redux/reducers/taxDuties/selectors';
import { getCountryListData } from 'redux/reducers/country/selectors';
import { ButtonCircularProgress } from 'ui/ButtonCircularProgress';
import { TSpecialTerritoryDialogProps } from './types';

const validationSchema = Yup.object({
	countryId: Yup.number().required('Country is required').min(1, 'Country is required'),
	specialTerritoryName: Yup.string()
		.required('Territory name is required')
		.max(255, 'Max 255 characters'),
	vatRate: Yup.number()
		.typeError('VAT rate must be a number')
		.required('VAT rate is required')
		.min(0, 'Must be >= 0')
		.max(100, 'Must be <= 100'),
	comments: Yup.string().max(1000, 'Max 1000 characters'),
});

export const SpecialTerritoryDialog: FC<TSpecialTerritoryDialogProps> = ({
	open,
	onClose,
	territoryToEdit,
}) => {
	const dispatch = useDispatch();
	const isEdit = !!territoryToEdit;

	const loading = useSelector(getSpecialTerritorySaveLoading);
	const saveSuccess = useSelector(getSpecialTerritorySaveSuccess);
	const countries = useSelector(getCountryListData);

	const formik = useFormik({
		initialValues: {
			countryId: territoryToEdit?.countryId ?? 0,
			specialTerritoryName: territoryToEdit?.specialTerritoryName ?? '',
			vatRate: territoryToEdit?.vatRate ?? ('' as unknown as number),
			isSupported: territoryToEdit?.isSupported ?? false,
			comments: territoryToEdit?.comments ?? '',
		},
		enableReinitialize: true,
		validationSchema,
		onSubmit: (values) => {
			const body = {
				countryId: Number(values.countryId),
				specialTerritoryName: values.specialTerritoryName,
				vatRate: Number(values.vatRate),
				isSupported: values.isSupported,
				comments: values.comments,
			};
			if (isEdit && territoryToEdit) {
				dispatch(updateSpecialTerritoryRequest({ id: territoryToEdit.id, body }));
			} else {
				dispatch(createSpecialTerritoryRequest(body));
			}
		},
	});

	useEffect(() => {
		if (saveSuccess) {
			dispatch(resetSaveSpecialTerritorySuccess());
			formik.resetForm();
			onClose();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [saveSuccess]);

	const handleClose = () => {
		formik.resetForm();
		onClose();
	};

	/** Special VAT territories belong to EU countries, so the picker offers only those. */
	const countryOptions = (countries ?? []).filter((country) => country.isEu);
	const selectedCountry = countryOptions.find((country) => country.id === formik.values.countryId);

	return (
		<Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
			<DialogTitle color="primary">
				{isEdit ? 'Edit Special VAT Territory' : 'Add New Special VAT Territory'}
			</DialogTitle>
			<DialogContent>
				<Box
					component="form"
					onSubmit={formik.handleSubmit}
					sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}
				>
					<Autocomplete
						options={countryOptions}
						value={selectedCountry ?? null}
						onChange={(event, country) =>
							formik.setFieldValue('countryId', country?.id ?? 0).catch(() => {})
						}
						getOptionLabel={(country) => country.name}
						isOptionEqualToValue={(country, selected) => country.id === selected.id}
						size="small"
						renderInput={(params) => (
							<TextField
								{...params}
								label="Country"
								name="countryId"
								onBlur={formik.handleBlur}
								error={formik.touched.countryId && !!formik.errors.countryId}
								helperText={formik.touched.countryId && formik.errors.countryId}
							/>
						)}
					/>
					<TextField
						label="Territory Name"
						name="specialTerritoryName"
						size="small"
						value={formik.values.specialTerritoryName}
						onChange={formik.handleChange}
						onBlur={formik.handleBlur}
						error={formik.touched.specialTerritoryName && !!formik.errors.specialTerritoryName}
						helperText={formik.touched.specialTerritoryName && formik.errors.specialTerritoryName}
					/>
					<TextField
						label="VAT Rate, %"
						name="vatRate"
						type="number"
						size="small"
						value={formik.values.vatRate}
						onChange={formik.handleChange}
						onBlur={formik.handleBlur}
						error={formik.touched.vatRate && !!formik.errors.vatRate}
						helperText={formik.touched.vatRate && formik.errors.vatRate}
					/>
					<FormControlLabel
						control={
							<Checkbox
								name="isSupported"
								checked={formik.values.isSupported}
								onChange={formik.handleChange}
							/>
						}
						label="Supported"
					/>
					<TextField
						label="Comments"
						name="comments"
						size="small"
						multiline
						minRows={2}
						value={formik.values.comments}
						onChange={formik.handleChange}
						onBlur={formik.handleBlur}
						error={formik.touched.comments && !!formik.errors.comments}
						helperText={formik.touched.comments && formik.errors.comments}
					/>
				</Box>
			</DialogContent>
			<DialogActions>
				<Button onClick={handleClose}>Cancel</Button>
				<Button
					variant="contained"
					disabled={loading}
					onClick={() => formik.handleSubmit()}
					startIcon={loading ? <ButtonCircularProgress /> : undefined}
				>
					{isEdit ? 'Save' : 'Create'}
				</Button>
			</DialogActions>
		</Dialog>
	);
};
